/**
 * The WagePro splash — the first frame of the app, and the only thing on
 * screen while the session boots and before `/login` paints.
 *
 * Visual: the brand green as a full-bleed SURFACE (not an accent), the mark
 * and wordmark optically centred, and one thin indeterminate bar. It is the
 * plainest member of the green-field/white-sheet family the employee screens
 * share, so the app's first frame already looks like the app.
 *
 * What it deliberately does NOT do: no artificial minimum display time. The
 * splash is on screen for exactly as long as the boot actually takes
 * (reading the stored native session, then `wageproAuth.me`), so this is a
 * presentation change only — it neither delays sign-in nor alters a single
 * auth state. `main.tsx`'s `LocalisedBootPlaceholder` renders it in exactly
 * the states it used to render a blank page in.
 *
 * Copy: the visible caption is unchanged from the placeholder it replaces —
 * "Loading your WagePro account…" only in the state where that is literally true
 * (see `LocalisedBootPlaceholder`'s docblock for why every other state must
 * stay silent). The `role="status"` line carries a neutral "Starting
 * WagePro…" for assistive tech in every state, because a screen with no
 * announceable text at all is worse than a generic one.
 */
import { useTranslation } from '@mukil/i18n';
import { isNative } from '../../lib/platform.js';

export function WageProSplash({
  caption,
}: {
  /** Visible caption. Omitted in states where no wording is accurate. */
  caption?: string;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <div className="wp-splash">
      <div className="wp-splash-brand">
        <img
          src="/brand/logo-mark-dark.svg"
          alt={t('brand.logo_alt')}
          width={56}
          height={56}
          className="wp-splash-mark"
        />
        {/* eslint-disable-next-line ledger/no-untranslated-jsx-text -- brand name */}
        <span className="wp-splash-wordmark">WagePro</span>
      </div>

      <div className="wp-splash-foot">
        {/* Indeterminate, because the boot has no measurable progress to
            report — a percentage here would be invented. `aria-hidden`: the
            status line below is what assistive tech reads. */}
        <div className="wp-splash-bar" aria-hidden="true">
          <span />
        </div>
        <p className="wp-splash-caption" role="status">
          {caption ?? <span className="sr-only">{t('boot.starting')}</span>}
        </p>
      </div>
    </div>
  );
}

/**
 * The pre-i18n first frame: the same green field with no text at all, so the
 * very first paint is never a flash of untranslated English (the reason the
 * blank placeholder this replaces existed) and never a white flash either.
 */
export function WageProSplashFrame(): React.JSX.Element {
  return (
    <div className="wp-splash">
      <div className="wp-splash-brand">
        <img
          src="/brand/logo-mark-dark.svg"
          alt=""
          width={56}
          height={56}
          className="wp-splash-mark"
        />
      </div>
    </div>
  );
}

/**
 * The neutral boot placeholder the WEB app shows — exactly what it rendered
 * before the splash existed: an empty, correctly-sized page, with the
 * "Loading your WagePro account…" line only in the state where that is true.
 *
 * A splash screen is a native-app idiom. In a browser tab the page is already
 * framed by the browser, the app is one of many tabs, and a full-bleed brand
 * takeover on every boot reads as a loading interstitial someone inflicted on
 * you. On a phone it is the opposite: the OS shows your icon, then your app,
 * and a blank white frame in between looks like a crash.
 */
function NeutralBootPlaceholder({
  caption,
}: {
  caption?: string;
}): React.JSX.Element {
  return (
    <div className="min-h-dvh flex items-center justify-center p-6 text-sm text-[color:var(--ink-muted)]">
      {caption ?? null}
    </div>
  );
}

/**
 * What to render while the session boots: the branded splash in the native
 * shell, the neutral placeholder on the web.
 *
 * `native` is injected (defaulting to the real runtime) so tests can exercise
 * both without a device.
 */
export function BootPlaceholder({
  caption,
  native = isNative(),
}: {
  caption?: string;
  native?: boolean;
}): React.JSX.Element {
  return native ? (
    <WageProSplash caption={caption} />
  ) : (
    <NeutralBootPlaceholder caption={caption} />
  );
}

/**
 * The pre-i18n FIRST frame, same split: the green field on native (so the
 * app opens into its own brand rather than a white flash), and the empty
 * neutral page on the web.
 */
export function BootPlaceholderFrame({
  native = isNative(),
}: {
  native?: boolean;
} = {}): React.JSX.Element {
  return native ? <WageProSplashFrame /> : <NeutralBootPlaceholder />;
}
